import type { ReactNode } from 'react';
import { groupRoleOf, type GroupAssignmentRole } from './GroupMemberEditor';
import { cn } from '../utils/cn';

// =============================================================================
// 관리자 추가·수정 모달의 '담당 그룹' 섹션.
//
// 구성 관리(GroupWorkspace)와 고객사 관리(bo/OrgAdmin)가 같은 모달(AdminFormModal)을 쓰면서
// 이 섹션만 각자 그리고 있었다 — 그래서 고객사 관리 쪽은 아예 섹션이 없었고, 계정을 만든 뒤
// 목록을 다시 읽어 '담당 그룹' 모달을 이어 여는 두 단계로 돌아갔다. 중간에 끊기면 계정만
// 생기고 그룹이 비어, 그 사람이 플레이리스트에 들어가면 권한 오류처럼 보였다(2026-09-17).
//
// 규칙(대교 2026-09-15·2026-09-17):
//   • 그룹별 역할은 고르지 않는다. 담당 그룹은 '어느 그룹인가'만 정하고, 그 안에서는 위의
//     '권한'으로 활동한다. 그래서 assignments 의 role 은 언제나 조직 권한과 같은 값이다.
//   • 조직 관리자(Owner)는 항상 전체 그룹이라 배정 대상이 아니다.
//   • 중앙 관리자 체크는 권한이 '그룹 관리자(Admin)'일 때만 켤 수 있다. 다른 권한에서도
//     자리는 그대로 두고 비활성 + 이유만 단다(조작부는 움직이지 않는다).
// =============================================================================

export type PickableGroup = { id: string; name: string };

/** 저장 요청에 실을 접근 필드. 권한이 정해지면 담당 그룹의 역할도 따라 정해진다. */
export function adminAccessFields(input: { role: string; central: boolean; picked: Set<string> }):
{ central: boolean; assignments: Array<{ groupId: string; role: GroupAssignmentRole }> } {
  if (input.role === 'Owner') return { central: false, assignments: [] };
  // 중앙 관리자는 모든 그룹을 보므로 개별 배정이 뜻이 없다 — 켜면 배정을 비운다.
  if (input.role === 'Admin' && input.central) return { central: true, assignments: [] };
  const groupRole = groupRoleOf(input.role);
  return { central: false,
    assignments: groupRole ? [...input.picked].map((groupId) => ({ groupId, role: groupRole })) : [] };
}

export function AdminGroupSection({ role, mode, groups, picked, central, onPick, onCentral, mark, note }: {
  role: string;
  mode: 'add' | 'edit';
  groups: PickableGroup[];
  picked: Set<string>;
  central: boolean;
  onPick: (next: Set<string>) => void;
  onCentral: (next: boolean) => void;
  /** 그룹 색·로고 표식(구성 관리에서만 쓴다). 없으면 이름만 적는다. */
  mark?: (group: PickableGroup) => ReactNode;
  /** 그룹 목록을 아직 못 읽었을 때 대신 보여 줄 한 줄. */
  note?: string;
}): JSX.Element {
  if (role === 'Owner') {
    return <p className="rounded border border-line bg-panel-2 px-3 py-2 text-[13px] text-text-dim">조직 관리자는 모든 그룹을 관리하므로 담당 그룹을 따로 지정하지 않습니다.</p>;
  }
  const centralAllowed = role === 'Admin';
  const centralOn = centralAllowed && central;
  const toggle = (groupId: string, on: boolean) => {
    const next = new Set(picked);
    if (on) next.add(groupId); else next.delete(groupId);
    onPick(next);
  };
  return (
    <fieldset className="border-0 p-0">
      <legend className="text-[13px] font-semibold text-text">담당 그룹 <span className="font-normal text-text-dim">({centralOn ? '전체' : `${picked.size}개`})</span></legend>
      <label className={cn('mt-2 flex items-center gap-2 rounded border border-line px-3 py-2 text-[14px]', centralAllowed ? 'cursor-pointer' : 'cursor-not-allowed opacity-50')}>
        <input type="checkbox" aria-label="중앙 관리자로 지정" checked={centralOn} disabled={!centralAllowed} onChange={(event) => onCentral(event.target.checked)} />
        <span className="min-w-0 flex-1">
          <span className="block">중앙 관리자 — 모든 그룹을 관리</span>
          <span className="block text-[12px] text-text-dim">{centralAllowed ? '끄면 아래에서 고른 그룹만 관리합니다.' : '권한을 그룹 관리자로 올리면 켤 수 있습니다.'}</span>
        </span>
      </label>
      {!centralOn && <>
        <p className="mt-2 text-[12px] text-text-dim">{mode === 'add' ? '고른 그룹의 관리자로 바로 배정합니다. 그룹 안에서는 위의 권한으로 활동합니다.' : '고른 그룹에서 위의 권한으로 활동합니다. 체크를 풀면 그 그룹에서 빠집니다.'}</p>
        {note ? <p className="mt-2 text-[13px] text-text-dim">{note}</p> : <div className="mt-2 grid grid-cols-2 gap-2">
          {groups.map((group) => (
            <label key={group.id} className={cn('flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-[14px]', picked.has(group.id) ? 'border-accent bg-accent-soft/40' : 'border-line')}>
              <input type="checkbox" aria-label={`${group.name} 담당`} checked={picked.has(group.id)} onChange={(event) => toggle(group.id, event.target.checked)} />
              {mark?.(group)}<span className="truncate">{group.name}</span>
            </label>
          ))}
          {groups.length === 0 && <p className="text-[13px] text-text-dim">아직 그룹이 없습니다.</p>}
        </div>}
      </>}
    </fieldset>
  );
}
